import { useCallback, useState } from "react";
import { createUseStyles } from "react-jss";
import type {
  AgreementSignature,
  Boat,
  NewReservation,
  Reservation,
} from "../../../../common/src";
import {
  DAMAGE_POLICY_LABELS,
  boatLoanDetailsFor,
  buildBoatLoanAgreement,
  newReservationSchema,
} from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { useAsyncData } from "../../hooks/useAsyncData";
import type { TripParams } from "../../routes";
import { routeToHash } from "../../routes";
import { useCurrentUser } from "../../session/SessionContext";
import type { VetBoatsTheme } from "../../theme";
import { trpc } from "../../trpc";
import { toFieldErrors } from "../../utils/fieldErrors";
import { formatDays, formatRange } from "../../utils/format";
import { AgreementSigner } from "../agreements/AgreementSigner";
import { GuestWaiverPanel } from "../reservations/GuestWaiverPanel";
import { Card } from "../shared/Card";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { EmptyState, LoadError, Loading } from "../shared/StatusViews";
import { useFormStyles, useLayoutStyles } from "../shared/styles";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  back: {
    display: "inline-block",
    marginBottom: 14,
    fontWeight: 600,
    textDecoration: "none",
  },
  summary: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: "4px 14px",
    margin: 0,
    "& dt": { color: theme.color.muted },
    "& dd": { margin: 0, fontWeight: 600 },
  },
}));

interface SignAndReserveProps {
  boat: Boat;
  trip: { startDate: string; endDate: string };
  initialPartySize: number;
  onReserved: (reservation: Reservation) => void;
}

const SignAndReserve = ({
  boat,
  trip,
  initialPartySize,
  onReserved,
}: SignAndReserveProps) => {
  const classes = useStyles();
  const form = useFormStyles();
  const user = useCurrentUser();
  const [partySize, setPartySize] = useState(String(initialPartySize));
  const [operatorNames, setOperatorNames] = useState(user.displayName);
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const reserve = useAction(
    useCallback(
      (input: NewReservation) => trpc.reservation.create.mutate(input),
      []
    )
  );

  const party = Number(partySize) || 1;
  // Rebuilt as details change, exactly as the server will rebuild it to check the signature.
  const doc = buildBoatLoanAgreement(
    boatLoanDetailsFor(boat, user, { ...trip, partySize: party, operatorNames })
  );

  const onSign = async (loanAgreement: AgreementSignature) => {
    const parsed = newReservationSchema.safeParse({
      boatId: boat.id,
      ...trip,
      partySize: Number(partySize),
      notes,
      operatorNames,
      loanAgreement,
    });
    if (!parsed.success) {
      setErrors(toFieldErrors(parsed.error));
      return;
    }
    setErrors({});
    const reservation = await reserve.run(parsed.data);
    if (reservation) {
      onReserved(reservation);
    }
  };

  return (
    <div className={form.stack}>
      <Card title={`Reserve ${boat.name}`}>
        <dl className={classes.summary}>
          <dt>Dates</dt>
          <dd>
            {formatRange(trip)} · {formatDays(trip)}
          </dd>
          <dt>Owner</dt>
          <dd>{boat.ownerName}</dd>
          <dt>Damage to the boat</dt>
          <dd>{DAMAGE_POLICY_LABELS[boat.damagePolicy]}</dd>
        </dl>
      </Card>
      <Card title="Sign the Boat Loan Agreement">
        <p className={form.intro}>
          Each trip has its own agreement naming the boat, dates, and who may
          operate it. Read it to the end, then sign to confirm your reservation.
          We'll email you a copy.
        </p>
        <AgreementSigner
          doc={doc}
          submitLabel="Sign and reserve"
          pending={reserve.pending}
          error={
            reserve.error ??
            (Object.keys(errors).length > 0
              ? "Please fix the fields above."
              : undefined)
          }
          onSign={onSign}
        >
          <div className={form.grid}>
            <Field
              label="Party size"
              htmlFor="reserve-party"
              hint={`Including you. Up to ${boat.capacity}.`}
              error={errors.partySize}
            >
              <input
                id="reserve-party"
                type="number"
                min={1}
                max={boat.capacity}
                className={form.control}
                value={partySize}
                onChange={(e) => setPartySize(e.target.value)}
              />
            </Field>
            <Field
              label="Who will operate the boat"
              htmlFor="reserve-operators"
              hint="Only these people may drive it. Separate names with commas."
              error={errors.operatorNames}
            >
              <input
                id="reserve-operators"
                className={form.control}
                value={operatorNames}
                onChange={(e) => setOperatorNames(e.target.value)}
              />
            </Field>
          </div>
          <Field
            label="Notes for the owner (optional)"
            htmlFor="reserve-notes"
            error={errors.notes}
          >
            <textarea
              id="reserve-notes"
              className={`${form.control} ${form.textarea}`}
              value={notes}
              maxLength={1000}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>
        </AgreementSigner>
      </Card>
    </div>
  );
};

/** Review the trip, sign its Boat Loan Agreement, and book it; then share the guest waiver link. */
export const ReservePage = ({
  boatId,
  trip,
}: {
  boatId: number;
  trip: TripParams;
}) => {
  const classes = useStyles();
  const layout = useLayoutStyles();
  const [reserved, setReserved] = useState<Reservation | undefined>(undefined);
  const load = useCallback(
    (signal: AbortSignal) => trpc.boat.get.query({ id: boatId }, { signal }),
    [boatId]
  );
  const boat = useAsyncData(load);
  const { startDate, endDate } = trip;

  const back = (
    <a
      className={classes.back}
      href={routeToHash({ page: "boat", boatId, trip })}
    >
      ← Back to the calendar
    </a>
  );
  if (!startDate || !endDate) {
    return (
      <div>
        {back}
        <EmptyState title="Pick your dates first">
          Choose a first and last day on the calendar.
        </EmptyState>
      </div>
    );
  }
  if (reserved) {
    return (
      <div className={layout.sections}>
        <Notice kind="success">
          {`Reserved ${reserved.boatName} for ${formatRange(reserved)}. A copy of your signed Boat Loan Agreement is on its way to your email.`}
        </Notice>
        <Card title="Next: every guest signs the waiver">
          <GuestWaiverPanel
            reservationId={reserved.id}
            partySize={reserved.partySize}
          />
        </Card>
        <a href={routeToHash({ page: "reservations" })}>
          Go to my reservations
        </a>
      </div>
    );
  }
  return (
    <div>
      {back}
      {boat.error && <LoadError message={boat.error} onRetry={boat.reload} />}
      {boat.loading && !boat.data && <Loading label="Loading boat…" />}
      {boat.data && (
        <SignAndReserve
          boat={boat.data}
          trip={{ startDate, endDate }}
          initialPartySize={trip.partySize ?? 1}
          onReserved={setReserved}
        />
      )}
    </div>
  );
};
